import { API_BASE } from "./api";

// Client for dubu-api's /pro/auth endpoints.
//
// Every call sends credentials: the session lives in httpOnly cookies, which JS cannot read
// (that's the point, XSS can't steal them). Consequence for deployment: because credentialed
// requests are involved, dubu-api's CORS_ORIGIN must name our exact origin. A wildcard '*'
// is rejected by browsers when credentials are included, so `CORS_ORIGIN=*` silently breaks
// sign-in while leaving public market data working.

export type ChainFamily = "evm" | "solana" | "aptos";

export interface NonceResponse {
  nonce: string;
  /** The exact string the wallet must sign. */
  message: string;
  caip10: string;
  chain_ref: string;
  expires_in: number;
}

export interface SessionWallet {
  id: string;
  chain_family: ChainFamily;
  chain_ref: string;
  address: string;
  caip10: string;
  verified_at: string;
}

export interface SessionUser {
  id: string;
  display_name: string | null;
  created_at: string;
}

export interface Session {
  user: SessionUser;
  wallets: SessionWallet[];
  active_wallet_id: string;
}

const req = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body.data as T;
};

export const getNonce = (family: ChainFamily, address: string) =>
  req<NonceResponse>(
    `/pro/auth/nonce?family=${family}&address=${encodeURIComponent(address)}`
  );

export interface VerifyBody {
  family: ChainFamily;
  address: string;
  signature: string;
  public_key?: string;
  /** Only for Aptos, whose wallets sign a wrapped message rather than ours. */
  signed_message?: string;
}

export const verifySignature = (body: VerifyBody) =>
  req<{ user: SessionUser; wallet: SessionWallet }>("/pro/auth/verify", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const getSession = () => req<Session>("/pro/auth/me");

export const logout = () => req<null>("/pro/auth/logout", { method: "POST" });

export const refreshSession = () =>
  req<{ user: SessionUser; wallet: SessionWallet }>("/pro/auth/refresh", {
    method: "POST",
  });
